import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Alert, Spinner } from "react-bootstrap";
import heroBg from "../assets/images/HeroBanner.png";
import { useNavigate, useLocation } from "react-router-dom";
import LicenseVerificationPaymentUtils from "../utils/licenseVerificationPaymentUtils";
import "../styles/LicenseVerificationPayment.css";

const LicenseVerificationPayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [licenseData, setLicenseData] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
const [paymentCompleted, setPaymentCompleted] = useState(false);

  React.useEffect(() => {
    const fetchLicenseData = async () => {
      try {
        setFetchingData(true);
        
        // Get license data from location state
        if (location.state?.licenseData) {
          const licenseId = location.state.licenseData._id;
          
          // Fetch updated data with payment info from backend
          const response = await LicenseVerificationPaymentUtils.getLicenseVerificationById(licenseId);
          
          if (response.success) {
            setLicenseData(response.data);
            
            // Set payment amount from backend or use default
            const amount = response.data.payment_info?.amount_rupees || response.data.amount || 1000;
            setPaymentAmount(amount);
          } else {
            throw new Error(response.message || 'Failed to fetch license verification details');
          }
        } else {
          setError("No license verification data found. Please submit the form first.");
        }
      } catch (error) {
        console.error('Error fetching license data:', error);
        setError(LicenseVerificationPaymentUtils.getErrorMessage(error));
      } finally {
        setFetchingData(false);
      }
    };

    fetchLicenseData();
  }, [location.state]);

  const handlePayment = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Load Razorpay script
      await LicenseVerificationPaymentUtils.loadRazorpayScript();

      // Create order
      const orderData = await LicenseVerificationPaymentUtils.createLicenseVerificationOrder(
        licenseData._id,
        paymentAmount
      );

      // Open Razorpay checkout
      await LicenseVerificationPaymentUtils.openLicenseVerificationCheckout({
        orderData,
        licenseData,
        onSuccess: async (paymentResponse) => {
          try {
            await verifyPayment(paymentResponse);
          } catch (error) {
            setError("Payment verification failed: " + error.message);
            setLoading(false);
          }
        },
        onFailure: (error) => {
          setError(error.message);
          setLoading(false);
        }
      });
    } catch (error) {
      setError(LicenseVerificationPaymentUtils.getErrorMessage(error));
      setLoading(false);
    }
  };

  const verifyPayment = async (paymentResponse) => {
    try {
      if (!LicenseVerificationPaymentUtils.validatePaymentResponse(paymentResponse)) {
        throw new Error("Invalid payment response");
      }

      const result = await LicenseVerificationPaymentUtils.verifyLicenseVerificationPayment(
        licenseData._id,
        {
          ...paymentResponse,
          amount_paise: paymentAmount * 100,
          amount_rupees: paymentAmount
        }
      );

if (result.success) {
  setSuccess("Payment successful! Your license verification request has been processed.");
  setPaymentCompleted(true); // ✅ IMPORTANT
  setLoading(false);

  setTimeout(() => {
    navigate('/license-verification-payment-success', {
      state: {
        licenseData,
        paymentDetails: {
          orderId: paymentResponse.razorpay_order_id,
          paymentId: paymentResponse.razorpay_payment_id,
          amountInRupees: paymentAmount
        }
      }
    });
  }, 2000);
}
 else {
        throw new Error(result.message || 'Payment verification failed');
      }
    } catch (error) {
      setError("Payment verification failed: " + LicenseVerificationPaymentUtils.getErrorMessage(error));
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (!licenseData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Fixed Hero Section */}
      <section
        className="hero-fixed"
        style={{ backgroundImage: `url(${heroBg})` }}>
        <div className="hero-scroll-content">
          <div className="hero-content-home poppins-regular">
            <h1 className="hero-heading">All India Occupational Therapists' Association</h1>
          </div>
        </div>
      </section>

      {/* Scrollable Content Container */}
      <div className="scrollable-content-home">
        <div className="content-container license-verification-payment-content-container">
          <section className="license-verification-payment-section">
            <Container>
              <h2 className="text-center license-verification-payment-heading mb-4">
                License Verification Payment
              </h2>

              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}

              {/* License Details */}
              <Card className="mb-4 shadow-sm license-verification-payment-card">
                <Card.Header>
                  <h4 className="mb-0">License Verification Details</h4>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <p><strong>Authority Name:</strong> {licenseData.authorityName}</p>
                      <p><strong>AIOTA Membership Number:</strong> {licenseData.aiotaMembershipNumber}</p>
                      <p><strong>ACOT/IOTR Number:</strong> {licenseData.acotIotrNumber}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>State Branch Name:</strong> {licenseData.stateBranchName}</p>
                      <p><strong>State Branch Membership Number:</strong> {licenseData.stateBranchMembershipNumber}</p>
                      <p><strong>Submission Date:</strong> {new Date(licenseData.createdAt).toLocaleDateString()}</p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Payment Information */}
              <Card className="mb-4 shadow-sm license-verification-payment-card">
                <Card.Header>
                  <h4 className="mb-0">Payment Information</h4>
                </Card.Header>
                <Card.Body>
                  <div className="license-verification-payment-summary">
                    <div className="summary-row">
                      <span>License Verification Processing Fee (including 18% GST):</span>
                      <span>₹{paymentAmount.toLocaleString()}</span>
                    </div>
                    <div className="summary-row total">
                      <span><strong>Total Amount:</strong></span>
                      <span><strong>₹{paymentAmount.toLocaleString()}</strong></span>
                    </div>
                  </div>
                  
                  <div className="license-verification-payment-actions mt-4 d-flex justify-content-center">
                    <Button
    variant="primary"
    size="lg"
    className="button-ad-submission-payment d-flex align-items-center justify-content-center"
    onClick={handlePayment}
     disabled={loading || paymentCompleted}
                    >
                     {loading ? (
      <>
        <Spinner
          as="span"
          animation="border"
          size="sm"
          role="status"
          aria-hidden="true"
          className="me-2"
          style={{
            width: "14px",
            height: "14px",
            borderWidth: "2px",
          }}
        />
        <span>Processing Payment...</span>
      </>
    ) : (
                        `Pay ₹${paymentAmount.toLocaleString()}`
                      )}
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              {/* Important Notes */}
              <Card className="mb-4 shadow-sm license-verification-payment-card">
                <Card.Header>
                  <h4 className="mb-0">Important Notes</h4>
                </Card.Header>
                <Card.Body>
                  <ul className="license-verification-payment-notes">
                    <li>Payment amount includes 18% GST as per government regulations.</li>
                    <li>Payment must be completed to process your license verification request.</li>
                    <li>License verification will be processed after successful payment.</li>
                    <li>You will receive confirmation via email once processing is complete.</li>
                    <li>All payments are processed securely through Razorpay.</li>
                    <li>For any payment issues, please contact the AIOTA office.</li>
                  </ul>
                </Card.Body>
              </Card>

              {/* Action Buttons */}
              <Row className="d-flex justify-content-center mb-4">
                <Col md={6} className="text-center">
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/license-verification-form')}
                    className="me-3"
                  >
                    Back to Form
                  </Button>
                
                </Col>
              </Row>
            </Container>
          </section>
        </div>
      </div>
    </>
  );
};

export default LicenseVerificationPayment;
